import "./SignIn.css";
import * as AppConfig from '../../app/config/constants'
import Button from "../../components/elements/Button";
import Input from "../../components/elements/Input";
import React, {Fragment, useEffect, useMemo, useState, useCallback, useRef} from "react";
import SidebarLayout from "../../components/templates/SidebarLayout.js";
import axios from "axios";
import useAuthToken from "../../hooks/useAuthToken";
import {Link, useHistory, useLocation} from "react-router-dom";
import useAuthenticatedUser from "../../hooks/useAuthenticatedUser";
import Loading from "../../components/modules/Loading";

function SignIn() {
  /*
  Responsible for ensuring global state has valid records for
  - auth_token
  - logged in user base data
   */

  const location = useLocation()
  const email_ref = useRef(AppConfig.SIGN_IN_USERNAME)
  const password_ref = useRef(AppConfig.SIGN_IN_PASSWORD)
  const [remember, set_remember] = useState(false);
  const remember_ref = useRef(remember)
  const [password_is_visible, set_password_is_visible] = useState(false);
  const history = useHistory();
  const [error_message, set_error_message] = useState("");
  const [is_loading, set_is_loading] = useState(true)
  const {auth_token, saveToken, clearToken} = useAuthToken();
  const {user_id, refreshCurrentUser} = useAuthenticatedUser()

  const redirect_to = useMemo(()=>{
    try {
      const params = new URLSearchParams(location.search)
      return params.get('redirect') || '/'
    } catch (ex) {
      return '/'
    }
  }, [location.search])

  const goRedirect = useCallback(() => {
    history.push({
      pathname: redirect_to,
    });
  }, [history, redirect_to]);

  const goOnboarding = useCallback(() => {
    history.push({
      pathname: "/onboarding",
    });
  }, [history]);

  const goToSignUp = useCallback(() => {
    history.push({
      pathname: "/signup",
    });
  }, [history]);

  const goToForgotPassword = useCallback(() => {
    history.push({
      pathname: "/forgot_password",
    });
  }, [history]);

  const redirectToOnboardingIfRequiredElseGoHome = useCallback( (user_id, token)=> {
    if (!token || !user_id) return;

    const URL =
      AppConfig.AUTH_API_BASE_URI + "users/user_informations/" + user_id;
    axios
      .get(URL, {
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      })
      .then((res) => {
        const userInformation = res.data.data.attributes;
        // const redirect_to_onboarding = userInformation.is_onboarding_complete === true
        const redirect_to_onboarding = userInformation.completion < 3 // random minimum number of steps. Revisit
        if (redirect_to_onboarding) {
          goOnboarding()
        } else {
          goRedirect()
        }
      })
      .catch(function (error) {
        console.log(error.response);
      });
  }, [goOnboarding, goRedirect])

  const signInUser = useCallback ( (email, password, remember) =>{
    const URL = AppConfig.AUTH_API_BASE_URI + "login";
    axios
      .post(URL, {
        user: {
          email: email,
          password: password,
        },
      })
      .then((res) => {
        if (res.status === 200) {
          const auth_token = res.headers.authorization ?
            res.headers.authorization :
            res.headers['x-amzn-remapped-authorization'];

          if (auth_token) {
            saveToken(auth_token, remember);
            redirectToOnboardingIfRequiredElseGoHome(user_id, auth_token);
          } else {
            clearToken()
            throw new Error("Server authentication succeeded but your browser is blocking returned authentication headers.")
          }
        }
      })
      .catch(function (error) {
        console.log(error);
        if (error.response.data.error === "Invalid Email or password.") {
          set_error_message("Email or password are incorrect.");
        } else if (
          error.response.data.error ===
          "You have one more attempt before your account is locked."
        ) {
          set_error_message(error.response.data.error);
        } else if (error.response.data.error === "Your account is locked.") {
          set_error_message(
            "Your account is locked. Please check your email for instructions."
          );
        }
      });

  }, [redirectToOnboardingIfRequiredElseGoHome, saveToken])

  const onSubmit = useCallback( (e)=>{
    e.preventDefault();
    signInUser(email_ref.current, password_ref.current, remember_ref.current);
  }, [signInUser])

  const SignInForm = () => {
    function togglePasswordVisibility() {
      set_password_is_visible(!password_is_visible);
    }

    const changeRemember = useCallback((e)=>{
        set_remember(!remember_ref.current)
        remember_ref.current = !remember_ref.current
    },[set_remember])

    var inputType = password_is_visible === true ? "text" : "password";

    return (
      <form className="sign-in-form" onSubmit={onSubmit} noValidate>
        <Input
          placeholder="Email"
          name="work-email"
          label="Work Email"
          // value={email}
          defaultValue={email_ref.current}
          onChange={(e) => {
            email_ref.current = e.target.value
          }}
          inputContainerClasses="sign-in-form__input"
          showArrow={false}
          readOnly={false}
        />
        <Input
          placeholder="Password"
          type={inputType}
          name="password"
          label="Password"
          defaultValue={password_ref.current}
          onChange={(e) => {
            password_ref.current = e.target.value
          }}
          inputContainerClasses="sign-in-form__input"
          showArrow={false}
          readOnly={false}
          showVisibilityToggle={true}
          iconOnClick={() => togglePasswordVisibility()}
        />

        <div className="sign-in-form__remember-me form-check">
          <input
            type="checkbox"
            className="form-check-input"
            onChange={changeRemember}
            checked={remember}
            id="remember_me_check"
          />
          <label
              className="form-check-label"
              htmlFor="remember_me_check"
          >Remember Me</label>
        </div>
        <span className="sign-in-form__remember-me-text">
          By checking this box, you will be able to skip entering your password
          and sign in using the code sent to your work email.
        </span>

        <div className="sign-in-btn">
          <Button
            label="Log In"
            active={true}
            class="long-btn filled"
          />
        </div>
      </form>
    );
  };

  const OtherActions = () => {
    return (
      <div className="sign-in__other-actions">
        <Button
          label="Recover Password"
          active={true}
          class="long-btn text-btn"
          onClick={() => goToForgotPassword()}
        />

        <div className="sign-in__or-element">
          <div className="sign-in__or-bar" />
          <span> OR </span>
          <div className="sign-in__or-bar" />
        </div>

        <div className="sign-in__outline-btn">
          <Button
            label="Create New Account"
            active={true}
            class="outline-btn"
            onClick={() => goToSignUp()}
          />
        </div>

        <div className="sign-in__or-element" style={{paddingTop: 50}}>
          <span>
            <Link to={{pathname: '/aboutus'}}>
              About Us
            </Link>&nbsp;-&nbsp;
            <Link to={{ pathname: "/contact" }}>
              Contact Us
            </Link>
          </span>
        </div>

      </div>
    );
  };

  const SignInContent = () => {
    return (
      <React.Fragment>
        <h4 className='input-container'>Find Healthcare Providers</h4>
        <p className="sign-in__subtitle">{error_message}</p>
        <SignInForm/>
        <OtherActions/>
      </React.Fragment>
    );
  };

  useEffect(() => {
    if (user_id) {
      goRedirect()
    } else if (auth_token) {
      refreshCurrentUser(auth_token).then(goRedirect).catch(()=>{
        set_is_loading(false)
      })
    } else {
      set_is_loading(false)
    }
  }, [auth_token, user_id]);

  if (is_loading) {
    return (<Loading/>)
  }

  return (
    <>
      <SidebarLayout
        signIn
        // sidebar={<SignInSideBar />}
        sidebarClasses="sign-in__sidebar"
        content={SignInContent()}
        contentClasses="sign-in__content"
        userStatus="logged-out"
        showFooter={false}
        hideNavivationBar={true}
      />
    </>
  );
}

export default SignIn;
